/**
 * <pmr-ramp-calculator> — longueur minimale d'une rampe selon la hauteur à franchir.
 * Valeurs réglementaires (réglages du thème, data-*) :
 *  - ERP neuf : arrêté du 20 avril 2017, art. 2 (5 % ; 8 % sur 2 m max ; 10 % sur 0,50 m max)
 *  - ERP existant : arrêté du 8 décembre 2014, art. 2 (6 % ; 10 % sur 2 m max ; 12 % sur 0,50 m max)
 *  - Domicile / véhicule : aucune norme ; on applique par prudence les valeurs ERP existant.
 */
class PmrRampCalculator extends HTMLElement {
  connectedCallback() {
    const n = (key) => Number(this.dataset[key]);
    this.rules = {
      new: {
        general: n('newGeneral'),
        mid: n('newMid'),
        midMax: n('newMidMax'),
        short: n('newShort'),
        shortMax: n('newShortMax'),
        landingFrom: n('newLandingFrom'),
      },
      existing: {
        general: n('exGeneral'),
        mid: n('exMid'),
        midMax: n('exMidMax'),
        short: n('exShort'),
        shortMax: n('exShortMax'),
        landingFrom: n('exLandingFrom'),
      },
    };
    this.thresholdMaxHeight = n('thresholdMaxHeight');
    this.portableMaxLength = n('portableMaxLength');
    this.bumpMax = n('bumpMax');

    this.input = this.querySelector('input[type="number"]');
    this.result = this.querySelector('.pmr-calculator__result');
    this.form = this.querySelector('form');

    this.form.addEventListener('input', () => this.update());
    this.form.addEventListener('change', () => this.update());
    this.form.addEventListener('submit', (e) => {
      e.preventDefault();
      this.update();
    });
  }

  get mode() {
    return this.querySelector('input[type="radio"]:checked')?.value || 'home';
  }

  /** Pente la plus forte autorisée pour la longueur obtenue (cm), selon les règles données. */
  compute(height, r) {
    const short = height / (r.short / 100);
    if (short <= r.shortMax) return { length: short, slope: r.short, rule: 'short' };
    const mid = height / (r.mid / 100);
    if (mid <= r.midMax) return { length: mid, slope: r.mid, rule: 'mid' };
    return { length: height / (r.general / 100), slope: r.general, rule: 'general' };
  }

  format(cm) {
    if (cm < 100) return `${cm} cm`;
    return `${(cm / 100).toFixed(2).replace('.', ',').replace(/,00$/, '')} m`;
  }

  update() {
    const height = parseFloat(String(this.input.value).replace(',', '.'));
    const $ = (sel) => this.querySelector(sel);
    const warning = $('[data-warning]');
    const note = $('[data-note]');
    warning.hidden = true;
    note.hidden = true;

    if (!height || height <= 0) {
      this.result.hidden = true;
      return;
    }

    const mode = this.mode;
    const rules = mode === 'new' ? this.rules.new : this.rules.existing;
    const { length, slope, rule } = this.compute(height, rules);
    const min = Math.ceil(length);
    const pct = String(slope).replace('.', ',');

    $('[data-length]').textContent = `Longueur minimale : ${this.format(min)}`;
    const label =
      mode === 'new' ? 'ERP neuf (arrêté du 20 avril 2017)' : mode === 'existing' ? 'ERP existant (arrêté du 8 décembre 2014)' : 'Valeurs ERP existant appliquées par prudence';
    const ruleText =
      rule === 'general'
        ? `pente courante de ${pct} %`
        : `tolérance de ${pct} % (rampe de ${this.format(rule === 'short' ? rules.shortMax : rules.midMax)} maximum)`;
    $('[data-detail]').textContent = `${label} : ${height.toString().replace('.', ',')} cm de hauteur, ${ruleText}.`;

    const notes = [];
    if (height <= this.bumpMax) {
      notes.push(`Jusqu'à ${this.bumpMax} cm, un simple ressaut chanfreiné ou à bord arrondi est admis : une rampe de seuil suffit souvent.`);
    }
    if (min > 1000 && slope >= rules.landingFrom) {
      notes.push('Au-delà de 10 m, un palier de repos de 1,20 m × 1,40 m est nécessaire tous les 10 m.');
    }
    notes.push('Prévoyez aussi un palier de repos de 1,20 m × 1,40 m en haut et en bas de la rampe.');
    if (mode === 'home') {
      notes.push("À domicile ou pour un véhicule, aucune norme ne s'impose : suivez aussi les préconisations du fabricant de la rampe.");
    }
    note.textContent = notes.join(' ');
    note.hidden = false;

    if (min > this.portableMaxLength && mode === 'home') {
      warning.textContent = 'Cette longueur dépasse les rampes portables courantes : contactez notre conseil technique pour une solution adaptée.';
      warning.hidden = false;
    }

    const cta = $('[data-cta]');
    const target = this.target(mode, height, min);
    cta.href = this.dataset[target.key];
    cta.textContent = target.label;

    this.result.hidden = false;
  }

  target(mode, height, length) {
    if (mode !== 'home') return { key: 'urlErp', label: 'Voir les packs accessibilité ERP' };
    if (height <= this.thresholdMaxHeight) return { key: 'urlThreshold', label: 'Voir les rampes de seuil' };
    if (length <= this.portableMaxLength) return { key: 'urlPortable', label: 'Voir les rampes PMR amovibles' };
    return { key: 'urlTelescopic', label: 'Voir les rampes télescopiques' };
  }
}

if (!customElements.get('pmr-ramp-calculator')) {
  customElements.define('pmr-ramp-calculator', PmrRampCalculator);
}
